import { Collapse, IconButton, Paper, Rating, Stack, Typography, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { SyntheticEvent, useCallback, useState } from 'react';

import CSATFollowUp from './CSATFollowUp';
import { CSAT_MAX_SCORE, CSAT_SCORE_LABELS } from './constants';

import { collectiveTracking } from 'modules/common/components/AnalyticsTracking/utils';
import CheckCircleFilledIcon from 'modules/common/components/Icons/CheckCircleFilledIcon';
import CloseXIcon from 'modules/common/components/Icons/CloseXIcon';
import SurveyStarIcon from 'modules/common/components/Icons/SurveyStarIcon';
import { CSAT_COMMENT_FIELD_ID, CSAT_SCORE_FIELD_ID, CSAT_STATUS_ID } from 'services/help-center/constants';
import useUpdateTicket from 'services/help-center/mutations/useUpdateTicket';
import useTicket from 'services/help-center/queries/useTicket';
import useZDUser from 'services/help-center/queries/useZDUser';

interface CSATSurveyProps {
    ticketId: number;
}

// The member's saved score, or null when there is none or Zendesk returned something off the scale.
function toScore(value: string | number | null | undefined): number | null {
    const score = Number(value);
    return Number.isInteger(score) && score >= 1 && score <= CSAT_MAX_SCORE ? score : null;
}

function getStarLabelText(value: number) {
    return `${value} star${value === 1 ? '' : 's'}, ${CSAT_SCORE_LABELS[value]}`;
}

export default function CSATSurvey({ ticketId }: Readonly<CSATSurveyProps>) {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [currentScore, setCurrentScore] = useState<number | null>(null);
    const [hoveredScore, setHoveredScore] = useState<number | null>(null);
    const [comment, setComment] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    const { data } = useTicket(ticketId, { refetchInterval: !submitted && 1000 * 60 * 5 });
    const { mutate: updateTicket, mutateAsync: updateTicketAsync, isPending: isSubmitting } = useUpdateTicket();
    const { data: zdUser } = useZDUser();

    const ticket = data?.ticket;
    const savedScore = toScore(ticket?.custom_fields?.find((field) => field.id === CSAT_SCORE_FIELD_ID)?.value);
    const score = currentScore ?? savedScore;

    const handleScoreChange = useCallback(
        (event: SyntheticEvent, value: number | null) => {
            // Rating clears to null when the selected star is clicked again; a rating can't be unset.
            if (value === null || !Number.isInteger(value) || value === score) {
                return;
            }
            setCurrentScore(value);
            // Saved on tap, not on Submit: the score is what reaches #nps even if the member never submits.
            updateTicket({
                ticketId,
                payload: { ticket: { custom_fields: [{ id: CSAT_SCORE_FIELD_ID, value }] } },
            });
        },
        [score, ticketId, updateTicket]
    );

    const handleHoverChange = useCallback((event: SyntheticEvent, value: number) => {
        // -1 means the pointer left the stars.
        setHoveredScore(Number.isInteger(value) && value > 0 ? value : null);
    }, []);

    const handleSubmit = useCallback(async () => {
        const trimmedComment = comment.trim();
        // The score rides along again: the save on tap may have failed or still be in flight.
        const customFields = [
            ...(score === null ? [] : [{ id: CSAT_SCORE_FIELD_ID, value: score }]),
            ...(trimmedComment ? [{ id: CSAT_COMMENT_FIELD_ID, value: trimmedComment }] : []),
        ];
        try {
            await updateTicketAsync({
                ticketId,
                payload: { ticket: { status: 'closed', custom_fields: customFields } },
            });
        } catch {
            // The form stays up with the member's comment intact, so they can try again.
            enqueueSnackbar("We couldn't send your feedback. Please try again.", { variant: 'error' });
            return;
        }
        setSubmitted(true);

        collectiveTracking.track({
            name: 'Csat Response Submitted',
            properties: {
                category: 'message_center',
                action: 'click',
                csat_rating: score,
                timestamp: new Date().toISOString(),
                user_id: zdUser?.id,
                additional_feedback: trimmedComment,
            },
        });
    }, [comment, enqueueSnackbar, score, ticketId, updateTicketAsync, zdUser?.id]);

    const handleDismiss = useCallback(() => setDismissed(true), []);

    // Submitting closes the ticket, so once submitted the thank-you stays until the member dismisses it.
    const awaitingRating = ticket?.status === 'solved' && ticket?.custom_status_id === CSAT_STATUS_ID;
    const visible = !dismissed && (submitted || awaitingRating);
    const caption = CSAT_SCORE_LABELS[hoveredScore ?? score ?? 0] ?? 'Tap a star to rate';

    return (
        <Collapse in={visible} mountOnEnter unmountOnExit>
            <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
                {submitted ? (
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                        <CheckCircleFilledIcon fill={theme.palette.success.main} />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            Thanks for sharing your feedback.
                        </Typography>
                        <IconButton aria-label="Close survey" size="small" onClick={handleDismiss}>
                            <CloseXIcon />
                        </IconButton>
                    </Stack>
                ) : (
                    <Stack spacing={1} sx={{ alignItems: 'center' }}>
                        <Typography variant="bodySemiBold" align="center">
                            How would you rate the support you received?
                        </Typography>
                        <Rating
                            name={`csat-rating-${ticketId}`}
                            value={score}
                            max={CSAT_MAX_SCORE}
                            onChange={handleScoreChange}
                            onChangeActive={handleHoverChange}
                            getLabelText={getStarLabelText}
                            icon={<SurveyStarIcon fill="green.400" fontSize="inherit" />}
                            emptyIcon={<SurveyStarIcon fill="cement.400" fontSize="inherit" />}
                        />
                        <Typography variant="caption" color="text.secondary" aria-live="polite">
                            {caption}
                        </Typography>
                        <Collapse in={score !== null} sx={{ width: '100%' }}>
                            {score !== null && (
                                <CSATFollowUp
                                    score={score}
                                    comment={comment}
                                    onCommentChange={setComment}
                                    onSubmit={handleSubmit}
                                    isSubmitting={isSubmitting}
                                />
                            )}
                        </Collapse>
                    </Stack>
                )}
            </Paper>
        </Collapse>
    );
}
